import React, { useState, type ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { JobCostEstimateLaunchProvider } from '@/contexts/JobCostEstimateLaunchContext';
import { useParams, useLocation } from 'react-router-dom';
import { JobCreate } from '@/pages/jobs/JobCreate';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';
import { TopBar } from '@/components/layout/TopBar';
import { PhoneAppBar } from '@/components/layout/PhoneAppBar';
import { DESKTOP_SHELL_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { PlatformAdminBar } from '@/components/platformAdmin/PlatformAdminBar';
import { OrgSwitcher } from '@/components/orgSwitcher/OrgSwitcher';
import { getNavItems, groupNavItems } from '@/utils/navigation';
import { profileLinkFor, splitTopBarNav } from '@/utils/topBarNav';
import { type NavContext } from '@/config/navConfig';

export const MainLayout: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { profile, signOut } = useAuth();
    const { organization, offices, departments, activeOfficeId, activeDepartmentId, setActiveOfficeId, setActiveDepartmentId } = useOrganization();
    const params = useParams();
    const [showJobModal, setShowJobModal] = useState(false);
    // Mobile-only: the sidebar collapses to a hamburger drawer below md. The
    // drawer renders the SAME Sidebar component, so nav stays one source.
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const location = useLocation();
    // WEB-111: md and up get the one-line top bar and no rail; below md the drawer and header stay. One tree is
    // mounted, never both, so the Platform admin chip and the org switcher exist once.
    const isDesktop = useMediaQuery(DESKTOP_SHELL_QUERY);

    // A nav tap should land you on the page, not back on the overlay -
    // the drawer closes on any route change (#125 review).
    useEffect(() => {
        setMobileNavOpen(false);
    }, [location.pathname]);

    // Escape closes the drawer, matching the repo's dialog convention
    // (AppDialog) - the overlay is functionally a modal.
    useEffect(() => {
        if (!mobileNavOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMobileNavOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mobileNavOpen]);

    // Sync URL with Context
    const urlOfficeId = params.officeId || null;
    const urlDepartmentId = params.departmentId || null;

    useEffect(() => {
        // Sync Office
        if (urlOfficeId) {
            setActiveOfficeId(urlOfficeId);
        } else {
            setActiveOfficeId(null);
        }

        // Sync Department
        if (urlDepartmentId) {
            setActiveDepartmentId(urlDepartmentId);
        } else {
            if (urlOfficeId) {
                setActiveDepartmentId(null);
            }
        }
    }, [urlOfficeId, urlDepartmentId, setActiveOfficeId, setActiveDepartmentId]);

    const effectiveOfficeId = urlOfficeId;
    const activeOffice = offices.find(o => o.id === effectiveOfficeId);

    // --- Dynamic Navigation ---
    let context: NavContext = 'global';
    if (urlOfficeId && urlDepartmentId) {
        context = 'department';
    } else if (urlOfficeId) {
        context = 'office';
    }

    const navItems = getNavItems({
        role: profile?.role || 'MEMBER',
        context,
        officeId: urlOfficeId || undefined,
        departmentId: urlDepartmentId || undefined,
        userProfile: profile
    });

    const navGroups = groupNavItems(navItems);

    return (
        <JobCostEstimateLaunchProvider>
        <div className={`flex ${isDesktop ? 'flex-col' : ''} bg-bg-primary mesh-gradient h-screen w-full overflow-hidden text-text-primary font-sans selection:bg-accent-electric/30`}>
            {isDesktop && (
                <TopBar
                    profile={profile}
                    organization={organization}
                    offices={offices}
                    departments={departments}
                    activeOfficeId={activeOfficeId}
                    activeDepartmentId={activeDepartmentId}
                    setActiveDepartmentId={setActiveDepartmentId}
                    signOut={signOut}
                    navGroups={navGroups}
                    onJobCreateClick={() => setShowJobModal(true)}
                    orgSwitcher={<OrgSwitcher />}
                    // WEB-100: the admin chip sits in the bar's right-hand group.
                    adminControl={<PlatformAdminBar />}
                />
            )}

            {!isDesktop && mobileNavOpen && (
                <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Navigation">
                    <div
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={() => setMobileNavOpen(false)}
                        aria-hidden="true"
                    />
                    {/* WEB-111 increment 4: the drawer carries what the phone bar no longer does - the org or office
                        title, the org switcher, the Platform admin chip, the job cost estimate and Create Job - above
                        the navigation. Mounted only while open, so the chip and the switcher still exist once. */}
                    <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] shadow-2xl flex flex-col bg-bg-primary">
                        <div data-testid="drawer-context" className="shrink-0 px-4 pt-4 border-b border-divider-subtle">
                            <Header
                                activeOfficeId={activeOfficeId}
                                activeOffice={activeOffice}
                                activeDepartmentId={activeDepartmentId}
                                departments={departments}
                                profile={profile}
                                onJobCreateClick={() => { setMobileNavOpen(false); setShowJobModal(true); }}
                                organizationName={organization?.name}
                                orgSwitcher={<OrgSwitcher />}
                                adminControl={<PlatformAdminBar />}
                            />
                        </div>
                        <div className="flex-1 min-h-0 flex">
                            <Sidebar
                                profile={profile}
                                organization={organization}
                                offices={offices}
                                departments={departments}
                                activeOfficeId={activeOfficeId}
                                activeDepartmentId={activeDepartmentId}
                                setActiveDepartmentId={setActiveDepartmentId}
                                signOut={signOut}
                                navGroups={navGroups}
                                expanded
                            />
                        </div>
                    </div>
                    <button
                        onClick={() => setMobileNavOpen(false)}
                        aria-label="Close navigation"
                        autoFocus
                        className="absolute top-4 right-4 p-2.5 rounded-xl bg-bg-secondary/80 border border-divider-subtle text-text-primary"
                    >
                        <X size={20} />
                    </button>
                </div>
            )}

            {/* Main Content */}
            <main className="flex-1 flex flex-col h-full min-h-0 min-w-0 overflow-hidden relative">
                {!isDesktop && (
                    <PhoneAppBar
                        home={splitTopBarNav(navGroups).tabs[0]?.to ?? '/'}
                        profile={profile}
                        departments={departments}
                        profileLink={profileLinkFor(activeOfficeId, activeDepartmentId)}
                        signOut={signOut}
                        onOpenNavigation={() => setMobileNavOpen(true)}
                    />
                )}

                <div data-testid="main-scroll" className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 md:px-8 pb-8 pt-2 md:pt-3 custom-scrollbar relative flex flex-col">
                    <ErrorBoundary>
                        {children}
                    </ErrorBoundary>
                </div>

                {showJobModal && <JobCreate onClose={() => setShowJobModal(false)} />}
            </main>
        </div>
        </JobCostEstimateLaunchProvider>
    );
};
